import React, { useEffect, useRef, useState } from 'react'

import { Disclosure } from 'api/lender/getLenderDisclosures'
import SecondCheckpointModal from 'components/CheckpointModal/SecondCheckpointModal'
import VerifiedIcon from 'components/Icons/VerifiedIcon'
import RoadrunnerDealershipAgreementNotice from 'components/notices/RoadrunnerDealershipAgreementNotice'
import { extendFieldInstructionAny, FormValues } from 'containers/FormBuilder'
import ReturnCustomerModal from 'containers/ReturnCustomer/ReturnCustomerModal'
import {
    ApplicationDataType,
    ActiveLoanType,
} from 'containers/ReturnCustomer/types/returnCustomerTypes'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import { useLender } from 'contexts/LenderContextProvider'
import useLenderDisclosures from 'hooks/useLenderDisclosures'
import useUser from 'hooks/useUser'
import LoadingPage from 'pages/LoadingPage/LoadingPage'
import DisclosureType from 'state/enums/DisclosureType'

import ApplicationFormBuilder, {
    ApplicationFormBuilderProps,
} from '../ApplicationFormBuilder'
import DeveloperFormDataQuickFill from '../DeveloperFormDataQuickFill/DeveloperFormDataQuickFill'
import {
    DISCLOSURE_LOAD_ERROR_MESSAGE,
    hasRequiredDisclosures,
} from '../disclosureUtils'
import {
    COAPPLICANT_TAG,
    isApplicationJointFunc,
    isCoapplicantAddressSameFunc,
    applicantCitizenshipStatusLinkingFunc,
    coapplicantCitizenshipStatusLinkingFunc,
} from '../sharedInstructions'
import submitPowersportsLikeApplication from '../submitPowersportsLikeApplication'
import { swapApplicantsFunc } from '../utils'
import { powersportsFormInstructions } from './powersportsApplicationFields'
import {
    Label,
    MainContainer,
    Title,
    Titlebar,
} from './PowersportsApplicationFormBuilder.styled'

type PowersportsApplicationFormProps = {
    overrideDefaultInitialValues?: ApplicationFormBuilderProps['overrideDefaultInitialValues']
    isLoading?: boolean
    isCopy?: boolean
    parentId?: number
    parentUuid?: string
    isReadOnly?: boolean
    crossoverApplication?: boolean
    vehicleTypeList?: string[]
    excludeVehicleTypesList?: string[]
}

const submitApplication = async (
    uuid: string,
    values: FormValues,
    setShowSubmissionModal: (state: boolean) => void,
    setShowReturnCustomerModal: (state: boolean) => void,
    setApplicationData: (state: ApplicationDataType) => void,
    setActiveLoans: (state: ActiveLoanType[]) => void,
    returnCustomerPsApp2FeatureFlag: boolean | undefined,
    finalize: boolean,
    isCopy?: boolean,
    parentId?: number,
    disclosures?: Disclosure[],
): Promise<string> => {
    return submitPowersportsLikeApplication({
        uuid,
        values,
        setShowSubmissionModal,
        setShowReturnCustomerModal,
        setApplicationData,
        setActiveLoans,
        returnCustomerPsApp2FeatureFlag,
        finalize,
        isCopy,
        parentId,
        disclosures,
    })
}

export default function PowersportsApplicationForm({
    overrideDefaultInitialValues,
    isLoading,
    isCopy,
    parentId,
    parentUuid,
    isReadOnly,
    crossoverApplication,
    vehicleTypeList,
    excludeVehicleTypesList,
}: Readonly<PowersportsApplicationFormProps>): JSX.Element {
    const [uuid, setUuid] = useState('')
    const [showSubmissionModal, setShowSubmissionModal] = useState(false)
    const finalizeRef = useRef(false)
    const [activeLoans, setActiveLoans] = useState<ActiveLoanType[]>([])

    const [
        isAgreementSignedWithRoadrunner,
        setIsAgreementSignedWithRoadrunner,
    ] = useState<boolean>()

    const featureFlags = useFeatureFlagContext()

    const dealershipLenderAgreementValidation =
        featureFlags?.dealershipLenderAgreementValidation

    const akHiEnabled = featureFlags?.akHiStateEnabled ?? false
    const { user } = useUser()
    const { id: lenderId } = useLender()

    const { disclosures, loading, retryAttemptsExhausted } =
        useLenderDisclosures(lenderId)

    const newInstructions = {
        ...powersportsFormInstructions(
            crossoverApplication,
            isReadOnly,
            akHiEnabled,
        ),
    }

    useEffect(() => {
        setIsAgreementSignedWithRoadrunner(
            user?.dealership?.isAgreementSignedWithRoadrunner,
        )
    }, [user?.dealership?.isAgreementSignedWithRoadrunner])

    const [applicationData, setApplicationData] = useState<
        ApplicationDataType | undefined
    >()

    const [developerOverrideData, setDeveloperOverrideData] =
        useState<ApplicationDataType>()

    const [showReturnCustomerModal, setShowReturnCustomerModal] =
        useState(false)

    const returnCustomerPsApp2FeatureFlag = featureFlags?.returnCustomerPsApp2

    if (isLoading) {
        return <LoadingPage />
    }

    const initialValuesOverride =
        developerOverrideData || overrideDefaultInitialValues

    const disclosuresLoaded = hasRequiredDisclosures(disclosures, [
        DisclosureType.APPLICATION_FORM_DISCLOSURE,
        DisclosureType.CUSTOMER_VERIFICATION_DISCLOSURE,
    ])

    const disclosureLoadFailed = !loading && retryAttemptsExhausted

    const canSubmit =
        !(
            dealershipLenderAgreementValidation === true &&
            isAgreementSignedWithRoadrunner === false
        ) &&
        !loading &&
        disclosuresLoaded

    if (crossoverApplication) {
        newInstructions.collateralFields.vehicle = extendFieldInstructionAny(
            newInstructions.collateralFields.vehicle,
            (instruction) => {
                return {
                    ...instruction,
                    config: {
                        ...instruction.config,
                        vehicleTypeList,
                        excludeVehicleTypesList,
                    },
                }
            },
        )
    }

    let collateralType = 'Powersports'

    if (vehicleTypeList?.length === 1) {
        switch (vehicleTypeList[0]) {
            case 'mower':
                collateralType = 'Mower'
                break
            case 'tractor':
                collateralType = 'Tractor'
                break
            case 'trailer':
                collateralType = 'Trailer'
                break
            case 'pwc':
                collateralType = 'PWC'
                break
        }
    }

    return (
        <MainContainer>
            <RoadrunnerDealershipAgreementNotice />
            {disclosureLoadFailed && (
                <p role="alert" data-testid="disclosure-load-error-message">
                    {DISCLOSURE_LOAD_ERROR_MESSAGE}
                </p>
            )}

            <ReturnCustomerModal
                applicationData={applicationData}
                activeLoans={activeLoans}
                isOpen={showReturnCustomerModal}
                handleClose={() => setShowReturnCustomerModal(false)}
            />
            <SecondCheckpointModal isOpen={showSubmissionModal} animated />

            {!isReadOnly && (
                <>
                    {!crossoverApplication && (
                        <Titlebar>
                            <Title data-testid="newapplication-title">
                                {isCopy ? 'Copy Into New' : 'New Application'}
                            </Title>
                            <Label data-testid="newapplication-label">
                                Instant decisions • No credit impact &nbsp;
                                <VerifiedIcon />
                            </Label>
                        </Titlebar>
                    )}
                </>
            )}

            <DeveloperFormDataQuickFill
                setDeveloperOverrideData={setDeveloperOverrideData}
            />

            <ApplicationFormBuilder
                onSubmit={async (values) => {
                    const newUuid = await submitApplication(
                        uuid,
                        values,
                        setShowSubmissionModal,
                        setShowReturnCustomerModal,
                        setApplicationData,
                        setActiveLoans,
                        returnCustomerPsApp2FeatureFlag,
                        finalizeRef.current,
                        isCopy,
                        parentId,
                        disclosures,
                    )

                    setUuid(newUuid)
                }}
                collateralType={collateralType}
                instructions={newInstructions}
                coapplicantTag={COAPPLICANT_TAG}
                isApplicationJointFunc={isApplicationJointFunc}
                showCitizenshipStatusDisclaimer={
                    applicantCitizenshipStatusLinkingFunc
                }
                showCoapplicantCitizenshipStatusDisclaimer={
                    coapplicantCitizenshipStatusLinkingFunc
                }
                showConsumerShoppedCollateralNotice={Boolean(
                    isCopy && parentUuid,
                )}
                swapApplicantsFunc={swapApplicantsFunc}
                overrideDefaultInitialValues={{
                    ...initialValuesOverride,
                }}
                enableReinitialize
                isCoapplicantAddressSameFunc={isCoapplicantAddressSameFunc}
                setFinalize={() => {
                    finalizeRef.current = true
                }}
                canSubmit={canSubmit}
                disclosureLoadFailed={disclosureLoadFailed}
                isReadOnly={isReadOnly}
                parentUuid={parentUuid}
                isCrossoverApplication={crossoverApplication}
            />
        </MainContainer>
    )
}

PowersportsApplicationForm.defaultProps = {
    overrideDefaultInitialValues: undefined,
    isCopy: false,
    isLoading: false,
    parentId: undefined,
    parentUuid: undefined,
    isReadOnly: false,
    crossoverApplication: false,
    vehicleTypeList: null,
    excludeVehicleTypesList: null,
}

export const testExports = {
    submitApplication,
}
